import React, { useState, useEffect } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import ConfirmationPopup from "../deletePopup/deletePopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPenToSquare,
  faPlus,
  faSearch,
  faEye,
  faTrash,
  faCalendarDays,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Same state-machine vocabulary as Event.status on the model — draft is where
// createEvent.mjs leaves anything created without --status, pending_approval
// exists for when a second reviewer is genuinely in the loop (not used today,
// see the Edit Status modal below), live/closed/cancelled cover the rest.
const STATUS_MAP = {
  draft: { cls: "bg-gray-100 text-gray-600", label: "Draft" },
  pending_approval: { cls: "bg-amber-100 text-amber-700", label: "Pending Approval" },
  approved: { cls: "bg-green-100 text-green-700", label: "Approved" },
  live: { cls: "bg-blue-100 text-blue-700", label: "Live" },
  closed: { cls: "bg-gray-200 text-gray-500", label: "Closed" },
  cancelled: { cls: "bg-red-100 text-red-600", label: "Cancelled" },
};

const EventsListing = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reload, setReload] = useState(false);
  const limit = 20;

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteButton, setShowDeleteButton] = useState(true);

  // Status edit — same shape as verificationRequests' Aadhaar approve/reject
  // modal: a status select plus a reason, logged to AuditLog either way. One
  // person can create AND approve today; this is the same mechanism a real
  // maker-checker split would use later, just not gated to a second user yet.
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusValue, setStatusValue] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    const fetchEvents = async () => {
      setLoading(true);
      const params = { page: currentPage, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;
      try {
        const res = await func.get(urls.LIST_EVENTS_URL, { params, signal: controller.signal });
        if (res?.status === 200) {
          setEvents(res?.data?.events || []);
          setTotalPages(res?.data?.totalPages || 1);
        } else {
          toast.error("Error fetching events");
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        toast.error("Network error");
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
    return () => controller.abort();
  }, [reload, currentPage]);

  const handleSearch = () => { setCurrentPage(1); setReload((r) => !r); };

  const openStatusModal = (ev) => {
    setStatusTarget(ev);
    setStatusValue(ev.status);
    setStatusReason("");
    setShowStatusPopup(true);
  };

  const saveStatus = async () => {
    if (!statusTarget?._id || !statusValue) return;
    setSavingStatus(true);
    try {
      const res = await func.put(`${urls.EDIT_EVENT_URL}${statusTarget._id}`, {
        status: statusValue,
        statusReason: statusReason.trim() || undefined,
      });
      if (res?.status === 200) {
        toast.success("Event status updated");
        setShowStatusPopup(false);
        setReload((r) => !r);
      } else {
        toast.error(res?.data?.message || "Failed to update status");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setShowDeleteButton(false);
    try {
      const res = await func.del(`${urls.DELETE_EVENT_URL}${deleteTarget._id}`);
      if (res?.status === 200) {
        toast.success("Event deleted");
        setShowDeletePopup(false);
        setDeleteTarget(null);
        setReload((r) => !r);
      } else {
        toast.error(res?.data?.message || "Failed to delete event");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete event");
    } finally {
      setShowDeleteButton(true);
    }
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faCalendarDays} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Events</h1>
              <p className="text-white/70 text-sm">Create, edit and approve Meets</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/events/add")}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Event
          </button>
        </div>

        {/* Search + status filter */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              maxLength={60}
              placeholder="Search by slug, label, city, community..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); setReload((r) => !r); }}
            className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_MAP).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            Search
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Event", "When", "Community / City", "Host", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : events.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faCalendarDays} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No events found
                    </td>
                  </tr>
                ) : (
                  events.map((ev, index) => {
                    const st = STATUS_MAP[ev.status] || { cls: "bg-gray-100 text-gray-500", label: ev.status };
                    return (
                      <tr key={ev._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-400 text-xs">{(currentPage - 1) * limit + index + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{ev.publicLabel}</p>
                          <p className="text-xs text-gray-400">/{ev.slug}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          {ev.startAt ? new Date(ev.startAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-700">{ev.community || ev.professionTag || "—"}</p>
                          <p className="text-xs text-gray-400">{ev.city || ""}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {ev.hostMatchmakerId?._phoneNumber || <span className="text-red-500">No host</span>}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => openStatusModal(ev)}
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls} hover:opacity-80 transition`}
                            title="Change status"
                          >
                            {st.label}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <ActionBtn icon={faEye} label="View" cls="bg-indigo-50 text-indigo-600 hover:bg-indigo-100" onClick={() => navigate(`/events/view/${ev._id}`)} />
                            <ActionBtn icon={faPenToSquare} label="Edit" cls="bg-gray-50 text-gray-600 hover:bg-gray-100" onClick={() => navigate(`/events/edit/${ev._id}`)} />
                            <ActionBtn icon={faTrash} label="Delete" cls="bg-red-50 text-red-500 hover:bg-red-100" onClick={() => { setDeleteTarget(ev); setShowDeletePopup(true); }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-5">
            <PagBtn onClick={() => setCurrentPage(1)} disabled={currentPage === 1} label="«" />
            <PagBtn onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} icon={faChevronLeft} />
            {currentPage > 3 && <span className="px-2 text-white/60">…</span>}
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 rounded-md text-sm font-medium transition ${
                  p === currentPage ? "bg-white text-indigo-700" : "text-white/70 hover:bg-white/10"
                }`}
              >
                {p}
              </button>
            ))}
            {currentPage < totalPages - 2 && <span className="px-2 text-white/60">…</span>}
            <PagBtn onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} icon={faChevronRight} />
            <PagBtn onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} label="»" />
          </div>
        )}
      </div>

      <ConfirmationPopup
        show={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        onCancel={() => setShowDeletePopup(false)}
        onAccept={handleDelete}
        showDeleteButton={showDeleteButton}
        question={<>Are you sure you want to delete <span className="font-bold italic">{deleteTarget?.publicLabel}</span>?</>}
        title="Delete Confirmation"
        acceptText="Yes, Delete"
        cancelText="Cancel"
      />

      {/* Change Status Modal */}
      {showStatusPopup && statusTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowStatusPopup(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Change Event Status</h3>
              <button onClick={() => setShowStatusPopup(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-xs text-gray-500">{statusTarget.publicLabel}</p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value)}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
                >
                  {Object.entries(STATUS_MAP).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                {statusValue === "approved" && (
                  <p className="text-xs text-gray-400 mt-1">Approved and live are treated identically everywhere in the code — either makes the event public and open for registration.</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reason (optional, logged to the audit trail)</label>
                <textarea
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. targeting and pricing checked, capacity confirmed..."
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowStatusPopup(false)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Cancel</button>
              <button
                onClick={saveStatus}
                disabled={savingStatus}
                className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ActionBtn = ({ icon, label, cls, onClick }) => (
  <div className="group relative">
    <button onClick={onClick} className={`flex items-center justify-center w-7 h-7 rounded-md transition ${cls}`}>
      <FontAwesomeIcon icon={icon} className="text-xs" />
    </button>
    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block text-xs bg-gray-800 text-white px-2 py-0.5 rounded shadow-lg whitespace-nowrap z-10">
      {label}
    </span>
  </div>
);

const PagBtn = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition ${
      disabled ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:bg-white/10"
    }`}
  >
    {icon ? <FontAwesomeIcon icon={icon} className="text-xs" /> : label}
  </button>
);

export default EventsListing;
